import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircleQuestion, X, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '';

const CATEGORIES = [
  { value: 'query', label: 'Query' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'complaint', label: 'Complaint' },
];

export default function HelpWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', category: 'query', message: '' });

  // Hide on chat pages to avoid overlapping audio/voice input buttons
  const isChatPage = /\/chat\//.test(location.pathname);
  if (isChatPage) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/feedback/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to send');
      setSent(true);
      setForm({ name: '', email: '', category: 'query', message: '' });
      setTimeout(() => { setSent(false); setIsOpen(false); }, 2500);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <motion.button
        onClick={() => { setIsOpen(!isOpen); setSent(false); }}
        className="fixed bottom-20 right-4 z-[60] w-12 h-12 rounded-full bg-[#C2410C] text-white shadow-lg flex items-center justify-center hover:bg-[#9A3412] transition-colors hover:scale-110 active:scale-95"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="help-widget-trigger"
        aria-label="Help & Queries"
      >
        {isOpen ? <X className="w-5 h-5" /> : <MessageCircleQuestion className="w-5 h-5" />}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-36 right-4 z-[60] w-[340px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-orange-100 overflow-hidden font-[Nunito]"
            data-testid="help-widget-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#C2410C] to-[#EA580C] px-5 py-4">
              <h3 className="text-white font-bold text-base font-[Outfit]">Help & Queries</h3>
              <p className="text-white/75 text-xs mt-0.5">We'd love to hear from you</p>
            </div>

            {sent ? (
              <div className="p-8 text-center" data-testid="help-widget-success">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-base font-semibold text-[#1F2937]">Message Sent!</p>
                <p className="text-sm text-[#9CA3AF] mt-1">We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 space-y-3" data-testid="help-widget-form">
                <div>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]/20 bg-gray-50 placeholder:text-gray-400"
                    data-testid="help-input-name"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]/20 bg-gray-50 placeholder:text-gray-400"
                    data-testid="help-input-email"
                    required
                  />
                </div>
                <div className="flex gap-1.5">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setForm({ ...form, category: cat.value })}
                      className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition-colors ${
                        form.category === cat.value
                          ? 'bg-[#C2410C] text-white border-[#C2410C]'
                          : 'bg-white text-[#4B5563] border-gray-200 hover:border-[#C2410C]/40'
                      }`}
                      data-testid={`help-category-${cat.value}`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
                <div>
                  <textarea
                    placeholder="Write your message here..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]/20 bg-gray-50 resize-none placeholder:text-gray-400"
                    data-testid="help-input-message"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-lg py-5 text-sm font-bold transition-all disabled:opacity-50"
                  data-testid="help-submit-btn"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
